import React from 'react';
import { Check, X } from 'lucide-react';

export const PasswordStrengthIndicator = ({ password, role = 'admin', validation }) => {
  if (!password) return null;

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'strong':
        return 'bg-green-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'weak':
        return 'bg-red-500';
      default:
        return 'bg-gray-300';
    }
  };

  const getStrengthText = (strength) => {
    switch (strength) {
      case 'strong':
        return 'Stark';
      case 'medium':
        return 'Mittel';
      case 'weak':
        return 'Schwach';
      default:
        return '';
    }
  };

  const getStrengthWidth = (strength) => {
    switch (strength) {
      case 'strong':
        return 'w-full';
      case 'medium':
        return 'w-2/3';
      case 'weak':
        return 'w-1/3';
      default:
        return 'w-0';
    }
  };

  const strength = validation?.strength || 'weak';
  const errors = validation?.errors || [];
  const isValid = validation?.valid || false;

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">Passwortstärke:</span>
          <span className={`font-medium ${
            strength === 'strong' ? 'text-green-600' :
            strength === 'medium' ? 'text-yellow-600' :
            'text-red-600'
          }`}>
            {getStrengthText(strength)}
          </span>
        </div>
        <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${getStrengthColor(strength)} ${getStrengthWidth(strength)}`}
          />
        </div>
      </div>

      {/* Requirements */}
      <div className="space-y-1 text-xs">
        {role === 'admin' && (
          <div className={`flex items-center gap-1 ${
            password.length >= 8 ? 'text-green-600' : 'text-gray-500'
          }`}>
            {password.length >= 8 ? (
              <Check className="h-3 w-3" />
            ) : (
              <X className="h-3 w-3" />
            )}
            <span>Mindestens 8 Zeichen</span>
          </div>
        )}
        
        {role === 'employee' && (
          <div className={`flex items-center gap-1 ${
            password.length >= 8 ? 'text-green-600' : 'text-gray-500'
          }`}>
            {password.length >= 8 ? (
              <Check className="h-3 w-3" />
            ) : (
              <X className="h-3 w-3" />
            )}
            <span>Mindestens 8 Zeichen</span>
          </div>
        )}

        {/* Recommendations for stronger passwords */}
        {password && (
          <>
            <div className={`flex items-center gap-1 ${
              /[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-400'
            }`}>
              {/[A-Z]/.test(password) ? (
                <Check className="h-3 w-3" />
              ) : (
                <X className="h-3 w-3" />
              )}
              <span>Großbuchstabe (empfohlen)</span>
            </div>
            
            <div className={`flex items-center gap-1 ${
              /[0-9]/.test(password) ? 'text-green-600' : 'text-gray-400'
            }`}>
              {/[0-9]/.test(password) ? (
                <Check className="h-3 w-3" />
              ) : (
                <X className="h-3 w-3" />
              )}
              <span>Zahl (empfohlen)</span>
            </div>
            
            <div className={`flex items-center gap-1 ${
              /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) ? 'text-green-600' : 'text-gray-400'
            }`}>
              {/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) ? (
                <Check className="h-3 w-3" />
              ) : (
                <X className="h-3 w-3" />
              )}
              <span>Sonderzeichen (empfohlen)</span>
            </div>
          </>
        )}
      </div>

      {/* Error messages */}
      {errors.length > 0 && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
          <ul className="list-disc list-inside space-y-1">
            {errors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
